import React, { useState } from 'react';
import { MessageCircle, Bot, CheckCircle, Monitor, Play } from 'lucide-react';
import { VideoModal } from './VideoModal';
export const HowItWorksSection = () => {
  const [selectedVideo, setSelectedVideo] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const steps = [{
    icon: <MessageCircle className="h-10 w-10 text-white" data-id="element-186" />,
    title: 'You Ask',
    description: "Simply tell our AI agent what you need help with - whether it's checking a setting, investigating an error, or fixing an issue."
  }, {
    icon: <Bot className="h-10 w-10 text-white" data-id="element-187" />,
    title: 'AI Takes Action',
    description: 'The AI agent immediately takes control, performing comprehensive troubleshooting and implementing fixes automatically.'
  }, {
    icon: <CheckCircle className="h-10 w-10 text-white" data-id="element-188" />,
    title: 'Get Results',
    description: 'Receive detailed results of the actions taken, with the option to escalate to our 24/7 AI Tech Support helpline if needed.'
  }];
  const macOsDemos = [{
    title: 'Check Software Updates',
    videoId: '1094649313',
    description: ['Automatically checks for system and app updates', 'Identifies available software updates', 'Shows update size and installation requirements']
  }, {
    title: 'Check IP Address',
    videoId: '1094649241',
    description: ['Quickly retrieves current IP address', 'Shows both IPv4 and IPv6 addresses', 'Displays network interface information']
  }, {
    title: 'Check Citrix Version',
    videoId: '1094648814',
    description: ['Identifies installed Citrix version', 'Verifies compatibility with system', 'Shows installation details']
  }, {
    title: 'Change Battery to Low Power Mode',
    videoId: '1094646517',
    description: ['Enables power-saving features', 'Optimizes battery performance', 'Extends battery life automatically']
  }];
  const windowsDemos = [{
    title: 'System Performance Check',
    description: ['Analyzes system resources', 'Identifies performance bottlenecks', 'Recommends optimization steps']
  }, {
    title: 'Network Diagnostics',
    description: ['Tests network connectivity', 'Troubleshoots connection issues', 'Optimizes network settings']
  }, {
    title: 'Storage Management',
    description: ['Analyzes disk space usage', 'Identifies large files', 'Recommends cleanup actions']
  }, {
    title: 'Driver Updates',
    description: ['Scans for outdated drivers', 'Identifies compatible updates', 'Manages driver installation']
  }];
  return <section id="how-it-works" className="py-20 px-4 bg-slate-50" data-id="element-189">
      <div className="max-w-7xl mx-auto" data-id="element-190">
        <div className="text-center mb-16" data-id="element-191">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-192">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-193">
            Our autonomous AI agent works silently in the background, keeping
            your systems running smoothly without interruption.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-8 justify-between relative mb-20" data-id="element-194">
          {steps.map((step, index) => <div key={index} className="flex flex-col items-center text-center md:w-1/3 relative z-10" data-id="element-195">
              <div className="bg-indigo-600 rounded-full p-5 mb-5" data-id="element-196">
                {step.icon}
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900" data-id="element-197">
                {step.title}
              </h3>
              <p className="text-gray-600" data-id="element-198">{step.description}</p>
            </div>)}
          <div className="hidden md:block absolute top-[52px] left-[20%] right-[20%] h-0.5 bg-indigo-200 z-0" data-id="element-199" />
        </div>
        <div className="mt-20" data-id="element-200">
          <div id="demos" className="flex items-center gap-3 mb-8" data-id="element-201">
            <Monitor className="h-6 w-6 text-indigo-600" data-id="element-202" />
            <h3 className="text-2xl font-bold text-gray-900" data-id="element-203">MacOS Demos</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20" data-id="element-204">
            {macOsDemos.map((demo, index) => <div key={index} className="bg-white p-6 rounded-xl shadow-md" data-id="element-205">
                <h4 className="text-xl font-semibold mb-6" data-id="element-206">{demo.title}</h4>
                <div className="aspect-w-16 aspect-h-9 mb-6 relative group cursor-pointer" onClick={() => setSelectedVideo({
              id: demo.videoId,
              title: demo.title
            })} data-id="element-207">
                  <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center rounded-lg" data-id="element-208">
                    <div className="bg-white rounded-full p-4 shadow-lg" data-id="element-209">
                      <Play className="h-8 w-8 text-indigo-600" data-id="element-210" />
                    </div>
                  </div>
                  <iframe src={`https://player.vimeo.com/video/${demo.videoId}?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=0`} width="100%" height="100%" frameBorder="0" title={demo.title} className="rounded-lg pointer-events-none" data-id="element-211" />
                </div>
                <ul className="list-disc list-inside text-gray-600 space-y-2" data-id="element-212">
                  {demo.description.map((bullet, i) => <li key={i} data-id="element-213">{bullet}</li>)}
                </ul>
              </div>)}
          </div>
          <div className="flex items-center gap-3 mb-8" data-id="element-214">
            <div className="h-6 w-6 text-indigo-600" data-id="element-215" />
            <h3 className="text-2xl font-bold text-gray-900" data-id="element-216">Windows Demos</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-217">
            {windowsDemos.map((demo, index) => <div key={index} className="bg-white p-6 rounded-xl shadow-md" data-id="element-218">
                <h4 className="text-xl font-semibold mb-4" data-id="element-219">{demo.title}</h4>
                <div className="aspect-w-16 aspect-h-10 mb-4 bg-slate-100 rounded-lg flex items-center justify-center" data-id="element-220">
                  <p className="text-gray-500" data-id="element-221">Demo Video Coming Soon</p>
                </div>
                <ul className="list-disc list-inside text-gray-600 space-y-2" data-id="element-222">
                  {demo.description.map((bullet, i) => <li key={i} data-id="element-223">{bullet}</li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </div>
      <VideoModal isOpen={!!selectedVideo} onClose={() => setSelectedVideo(null)} videoId={selectedVideo?.id || ''} title={selectedVideo?.title || ''} data-id="element-224" />
    </section>;
};